import moment from 'moment-timezone';

export const DEFAULT_TIME_ZONE = 'America/Sao_Paulo';

export const BRAZIL_TIME_ZONE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'America/Noronha', label: 'Fernando de Noronha (UTC-02:00)' },
  { value: 'America/Araguaina', label: 'Tocantins (UTC−03:00)' },
  { value: 'America/Bahia', label: 'Bahia (UTC-03:00)' },
  { value: 'America/Belem', label: 'Pará (UTC−03:00)' },
  { value: 'America/Fortaleza', label: 'Ceará (UTC−03:00)' },
  { value: 'America/Maceio', label: 'Alagoas e Sergipe (UTC−03:00)' },
  { value: 'America/Recife', label: 'Pernambuco (UTC-03:00)' },
  { value: 'America/Santarem', label: 'Oeste do Pará (UTC−03:00)' },
  { value: DEFAULT_TIME_ZONE, label: 'Brasília, Sul e Sudeste (UTC−03:00)' },
  { value: 'America/Boa_Vista', label: 'Roraima (UTC-04:00)' },
  { value: 'America/Campo_Grande', label: 'Mato Grosso do Sul (UTC-04:00)' },
  { value: 'America/Cuiaba', label: 'Mato Grosso (UTC-04:00)' },
  { value: 'America/Manaus', label: 'Amazonas (UTC-04:00)' },
  { value: 'America/Porto_Velho', label: 'Rondônia (UTC−04:00)' },
  { value: 'America/Eirunepe', label: 'Oeste do Amazonas (UTC-05:00)' },
  { value: 'America/Rio_Branco', label: 'Acre (UTC-05:00)' },
];

type DateTimeValue = Date | string | number;

const DATE_TIME_LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const EXPLICIT_OFFSET_PATTERN = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

export const isValidTimeZone = (value: unknown): value is string => (
  typeof value === 'string'
  && value.trim().length > 0
  && Boolean(moment.tz.zone(value.trim()))
);

export const normalizeTimeZone = (value: unknown): string => {
  if (!isValidTimeZone(value)) {
    return DEFAULT_TIME_ZONE;
  }

  return value.trim();
};

export const getTimeZoneLabel = (value: unknown): string => {
  const timeZone = normalizeTimeZone(value);
  return BRAZIL_TIME_ZONE_OPTIONS.find((option) => option.value === timeZone)?.label ?? timeZone;
};

const resolveTimeZoneForConversion = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return DEFAULT_TIME_ZONE;
  }

  if (!isValidTimeZone(value)) {
    throw new Error('Fuso horário IANA inválido.');
  }

  return value.trim();
};

const asValidInstant = (value: DateTimeValue, timeZone: string): moment.Moment => {
  if (typeof value === 'string') {
    const normalizedValue = value.trim();
    if (DATE_TIME_LOCAL_PATTERN.test(normalizedValue)) {
      throw new Error(
        'Data/hora sem offset não representa um instante. Use parseDateTimeLocalInTimeZone antes de formatar.',
      );
    }

    if (DATE_ONLY_PATTERN.test(normalizedValue)) {
      const civilDate = moment.tz(normalizedValue, 'YYYY-MM-DD', true, timeZone);
      if (!civilDate.isValid() || civilDate.format('YYYY-MM-DD') !== normalizedValue) {
        throw new Error('Data inválida.');
      }
      return civilDate;
    }

    if (!EXPLICIT_OFFSET_PATTERN.test(normalizedValue)) {
      throw new Error('O timestamp precisa conter Z ou um offset UTC explícito.');
    }

    const explicitInstant = moment.parseZone(normalizedValue);
    if (!explicitInstant.isValid()) {
      throw new Error('Data/hora inválida.');
    }
    return explicitInstant;
  }

  const instant = moment(value);
  if (!instant.isValid()) {
    throw new Error('Data/hora inválida.');
  }

  return instant;
};

export const formatInTimeZone = (
  value: DateTimeValue,
  format: string,
  timeZone: unknown = DEFAULT_TIME_ZONE,
): string => {
  const resolvedTimeZone = resolveTimeZoneForConversion(timeZone);
  return asValidInstant(value, resolvedTimeZone).tz(resolvedTimeZone).format(format);
};

export const toDateTimeLocalInTimeZone = (
  value: DateTimeValue,
  timeZone: unknown = DEFAULT_TIME_ZONE,
): string => formatInTimeZone(value, 'YYYY-MM-DDTHH:mm', timeZone);

export const addCalendarDaysInTimeZone = (
  value: DateTimeValue,
  days: number,
  timeZone: unknown = DEFAULT_TIME_ZONE,
): Date => {
  if (!Number.isInteger(days)) {
    throw new Error('A quantidade de dias precisa ser inteira.');
  }

  const resolvedTimeZone = resolveTimeZoneForConversion(timeZone);
  return asValidInstant(value, resolvedTimeZone).tz(resolvedTimeZone).add(days, 'day').toDate();
};

const findMatchingUtcInstants = (
  localTimestamp: number,
  normalizedValue: string,
  format: string,
  timeZone: string,
): number[] => {
  const zone = moment.tz.zone(timeZone);
  if (!zone) {
    return [];
  }

  const offsets = new Set<number>();
  for (let hours = -36; hours <= 36; hours += 6) {
    offsets.add(zone.utcOffset(localTimestamp + (hours * 60 * 60 * 1000)));
  }

  return Array.from(offsets)
    .map((offsetMinutes) => localTimestamp + (offsetMinutes * 60 * 1000))
    .filter((candidate) => (
      zone.utcOffset(candidate) === ((candidate - localTimestamp) / (60 * 1000))
      && moment.tz(candidate, timeZone).format(format) === normalizedValue
    ));
};

export const parseDateTimeLocalInTimeZone = (
  value: string,
  timeZone: unknown = DEFAULT_TIME_ZONE,
): Date => {
  const match = DATE_TIME_LOCAL_PATTERN.exec(value.trim());
  if (!match) {
    throw new Error('Informe uma data e um horário válidos.');
  }

  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue, millisecondValue] = match;
  const hasSeconds = secondValue !== undefined;
  const hasMilliseconds = millisecondValue !== undefined;
  const format = hasMilliseconds
    ? `YYYY-MM-DDTHH:mm:ss.${'S'.repeat(millisecondValue.length)}`
    : hasSeconds
      ? 'YYYY-MM-DDTHH:mm:ss'
      : 'YYYY-MM-DDTHH:mm';
  const normalizedValue = hasMilliseconds
    ? `${yearValue}-${monthValue}-${dayValue}T${hourValue}:${minuteValue}:${secondValue}.${millisecondValue}`
    : hasSeconds
      ? `${yearValue}-${monthValue}-${dayValue}T${hourValue}:${minuteValue}:${secondValue}`
      : `${yearValue}-${monthValue}-${dayValue}T${hourValue}:${minuteValue}`;
  const resolvedTimeZone = resolveTimeZoneForConversion(timeZone);
  const parsed = moment.tz(normalizedValue, format, true, resolvedTimeZone);

  if (!parsed.isValid() || parsed.format(format) !== normalizedValue) {
    throw new Error('O horário informado não existe no fuso da clínica ou a data é inválida.');
  }

  const localTimestamp = Date.UTC(
    Number(yearValue),
    Number(monthValue) - 1,
    Number(dayValue),
    Number(hourValue),
    Number(minuteValue),
    Number(secondValue || 0),
    Number((millisecondValue || '').padEnd(3, '0') || 0),
  );
  const matchingInstants = findMatchingUtcInstants(
    localTimestamp,
    normalizedValue,
    format,
    resolvedTimeZone,
  );

  if (matchingInstants.length > 1) {
    throw new Error('O horário informado é ambíguo devido a uma mudança de horário de verão. Escolha outro horário.');
  }

  if (matchingInstants.length === 0) {
    throw new Error('O horário informado não existe no fuso da clínica ou a data é inválida.');
  }

  return new Date(matchingInstants[0]);
};

export const parseDateAndTimeInTimeZone = (
  dateValue: string,
  timeValue: string,
  timeZone: unknown = DEFAULT_TIME_ZONE,
): Date => parseDateTimeLocalInTimeZone(`${dateValue}T${timeValue}`, timeZone);
